import React, { forwardRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import SmartlistAPI from "../../api/SmartlistAPI";
import { FormProviderWithTranslations } from "../../contexts/FormTranslationsContext";
import {
  apiToFormTranslations,
  createTranslationResponseToFormField,
  displayTranslationsFormSchema,
  formToApiTranslations,
} from "../../utils/translationAdapters/display";
import Input from "../form/Input";
import DeletePicturesButton from "../formTranslations/DeletePicturesButton";
import FormPictures from "../formTranslations/FormPictures";
import FormTranslations from "../formTranslations/FormTranslations";
import FormTranslationsHeader from "../formTranslations/FormTranslationsHeader";

import type { LanguageCode } from "../../api/ProjectAPI.types";
import type {
  CreateDisplayTranslationResponse,
  GetDisplayResponse,
} from "../../api/SmartlistAPI.types";
import type { PictureKeys } from "../../constants/ratio";
import type {
  FormTranslationsHandle,
  PageFieldArray,
} from "../../contexts/FormTranslationsContext";
import type { DisplayTranslationFormField } from "../../utils/translationAdapters/display";
import type { UploadPictureState } from "../pictures/ModuleImageUploadWithTranslation";

export type DisplayTranslationsHandle = FormTranslationsHandle<
  ReturnType<typeof formToApiTranslations>
>;

interface DisplayTranslationsProps {
  display: GetDisplayResponse;
}

const DisplayTranslations = forwardRef<
  DisplayTranslationsHandle,
  DisplayTranslationsProps
>(({ display }, ref) => {
  const { t } = useTranslation("form");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const queryClient = useQueryClient();

  const getDisplayQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getDisplay,
    params: [{ uuid: display.uuid, projectSlug }],
  });

  const methods = useForm<
    PageFieldArray<DisplayTranslationFormField>,
    unknown,
    PageFieldArray<DisplayTranslationFormField>
  >({
    mode: "onChange",
    resolver: zodResolver(displayTranslationsFormSchema),
    defaultValues: {
      translations: apiToFormTranslations(display.translations),
    },
  });

  const uploadPicture = useMutation({
    mutationFn: async ({
      pictureFile,
      translationId,
      pictureFormat,
    }: {
      pictureFile: File;
      translationId: string;
      pictureFormat: PictureKeys;
    }) =>
      SmartlistAPI.uploadPicture({
        smartlistId: display.uuid,
        pictureFile,
        translationId,
        pictureFormat,
        projectSlug,
        isDisplay: true,
      }),
    onSuccess(data, variables) {
      const updatedTranslations = display.translations.map((translation) => {
        if (translation.uuid === variables.translationId) {
          return { ...translation, picture: data.picture };
        }
        return translation;
      });

      const updatedDisplay = { ...display, translations: updatedTranslations };

      queryClient.setQueryData(getDisplayQuery.queryKey, updatedDisplay);

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, data.picture);
      }
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handlePictureUpload = ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }): void => {
    uploadPicture.mutate({ pictureFile: file, translationId, pictureFormat });
  };

  const uploadPictureState: UploadPictureState = {
    isLoading: uploadPicture.isLoading,
    pictureFormat: uploadPicture.variables?.pictureFormat,
    translationId: uploadPicture.variables?.translationId,
  };

  const deletePicture = useMutation({
    mutationFn: async ({ translationId }: { translationId: string }) =>
      SmartlistAPI.deletePicture({
        smartlistId: display.uuid,
        translationId,
        projectSlug,
        isDisplay: true,
      }),
    onSuccess(_data, variables) {
      const updatedTranslations = display.translations.map((translation) =>
        translation.uuid === variables.translationId
          ? { ...translation, picture: null }
          : translation,
      );

      const updatedDisplay = { ...display, translations: updatedTranslations };

      queryClient.setQueryData(getDisplayQuery.queryKey, updatedDisplay);

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, undefined);
      }

      toast.success(t("form:deletePicturesSuccess"));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleAddLanguage = async ({
    languageCode,
    languageUuid,
  }: {
    languageCode: LanguageCode;
    languageUuid: string;
  }): Promise<CreateDisplayTranslationResponse | null> => {
    if (!languageUuid) return null;

    try {
      const response = await SmartlistAPI.createDisplayTranslation({
        displayId: display.uuid,
        languageUuid,
        projectSlug,
      });

      queryClient.setQueryData(getDisplayQuery.queryKey, {
        ...display,
        translations: [
          ...display.translations,
          {
            uuid: response.uuid,
            default: response.default,
            language_uuid: response.language_uuid,
            language: languageCode,
            title: response.title ?? "",
            url: response.url ?? "",
            picture: null,
          },
        ],
      });

      return response;
    } catch (error) {
      console.error("Error creating display translation:", error);
      toast.error(t("genericError", { ns: "error" }));
      return null;
    }
  };

  const updateDisplayMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: GetDisplayResponse;
    }) => SmartlistAPI.updateDisplay({ uuid, data, projectSlug }),
    onSuccess: (updatedDisplay) => {
      toast.success(t("editor-notif:saveSuccess", { ns: "editor-notif" }));
      queryClient.setQueryData(getDisplayQuery.queryKey, updatedDisplay);
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleDeleteLanguage = async (
    updatedFormTranslations: ReturnType<typeof apiToFormTranslations>,
  ): Promise<void> => {
    const apiTranslations = formToApiTranslations(updatedFormTranslations);

    await updateDisplayMutation.mutateAsync({
      uuid: display.uuid,
      data: {
        ...display,
        translations:
          apiTranslations.translations as unknown as GetDisplayResponse["translations"],
        translations_indexes: apiTranslations.translations_indexes,
      },
    });
  };

  return (
    <div className="bg-card flex flex-col rounded-[0.3125rem] p-[1.5625rem] drop-shadow-md">
      <FormProviderWithTranslations
        formToApiTranslations={formToApiTranslations}
        handleRef={ref}
        methods={methods}
        onAddLanguage={handleAddLanguage}
        onDeleteLanguage={handleDeleteLanguage}
        toFormField={createTranslationResponseToFormField}
      >
        <FormTranslationsHeader />
        <FormTranslations>
          <Input
            isFullWidth
            label={t("fields.title")}
            name="title"
            type="text"
          />
          <Input
            isFullWidth
            label={t("fields.url", "URL")}
            name="url"
            type="text"
          />
        </FormTranslations>
        <div>
          <div className="mb-0 flex items-center gap-[0.625rem]">
            <div className="font-semibold whitespace-nowrap md:whitespace-normal">
              {t("form:fields.pictures")} ({t("form:fields.picturesLimitation")}
              )
            </div>
            <DeletePicturesButton onDelete={deletePicture.mutateAsync} />
          </div>
          <FormPictures
            item={display}
            itemType="smartlists"
            onUpload={handlePictureUpload}
            uploadPictureState={uploadPictureState}
          />
        </div>
      </FormProviderWithTranslations>
    </div>
  );
});

DisplayTranslations.displayName = "DisplayTranslations";

export default DisplayTranslations;
